import { useEffect, useState } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Save } from "lucide-react";

export default function AdminScoring() {
  const [rules, setRules] = useState([]);
  const load = () => api.get("/admin/scoring/rules").then((r) => setRules(r.data));
  useEffect(() => { load(); }, []);

  const save = async (rule, patch) => {
    try {
      await api.patch(`/admin/scoring/rules/${rule.id}`, patch);
      toast.success("Règle mise à jour");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <PortalLayout sidebar="admin">
      <div>
        <h1 className="text-3xl font-bold text-navy md:text-4xl"><Trophy className="mr-2 inline h-7 w-7 text-amber-500" />Scoring communauté</h1>
        <p className="mt-1 text-neutral-600">Paramétrez les points attribués aux membres selon leur engagement. Les modifications sont appliquées immédiatement.</p>
      </div>

      <div className="mt-6 space-y-3">
        {rules.map((r) => (
          <RuleRow key={r.id} rule={r} onSave={save} />
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-wine-100 bg-wine-50/40 p-5 text-xs text-neutral-700" data-testid="scoring-help">
        <strong>Comment ça marche :</strong> chaque membre voit son score total + le détail dans son portail (<code>/portal/score</code>). Le score est recalculé en temps réel à partir des données de la plateforme. Désactivez une règle pour la retirer du calcul sans la supprimer.
      </div>
    </PortalLayout>
  );
}

function RuleRow({ rule, onSave }) {
  const [pts, setPts] = useState(rule.points);
  const [enabled, setEnabled] = useState(rule.enabled);
  const dirty = pts !== rule.points || enabled !== rule.enabled;

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-neutral-100 bg-white p-4" data-testid={`scoring-rule-${rule.id}`}>
      <label className="inline-flex items-center gap-2 text-xs font-semibold text-neutral-700">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4 accent-wine-500" data-testid={`rule-enabled-${rule.id}`} />
        {enabled ? "Activée" : "Désactivée"}
      </label>
      <div className="flex-1 min-w-[200px]">
        <div className={`font-bold ${enabled ? "text-neutral-900" : "text-neutral-400 line-through"}`}>{rule.label}</div>
        <div className="text-xs text-neutral-500">{rule.description}</div>
        <div className="mt-0.5 text-[10px] text-neutral-400">Clé : <code>{rule.id}</code></div>
      </div>
      <div className="inline-flex items-center gap-1">
        <input type="number" value={pts} onChange={(e) => setPts(parseInt(e.target.value) || 0)} className="w-20 rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid={`rule-points-${rule.id}`} />
        <span className="text-xs text-neutral-500">pts</span>
      </div>
      <button onClick={() => onSave(rule, { points: pts, enabled })} disabled={!dirty} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-4 py-2 text-xs font-bold text-white hover:bg-wine-700 disabled:opacity-40" data-testid={`rule-save-${rule.id}`}>
        <Save className="h-3.5 w-3.5" /> Enregistrer
      </button>
    </div>
  );
}
